import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { CreateDocumentsDto, FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ProcessingFile } from '@dealstack/frontend/shared/components/Documents/types';
import {
	DocumentFolderEntityMap,
	DocumentLinkTarget,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';
import { Dispatch, SetStateAction } from 'react';

type DocumentLinkToCreate = { documentId: string } & DocumentLinkTarget;

export const useUploadDocumentsController = ({
	dealId,
	target,
	processingFiles,
	setProcessingFiles,
	setIsUploadingFiles,
	relatedFilesInDeal,
	setNbrFilesUploaded,
	setNbrFiles,
}: {
	dealId: string;
	target?: DocumentLinkTarget;
	processingFiles: ProcessingFile[];
	setProcessingFiles: (processingFiles: ProcessingFile[]) => void;
	setIsUploadingFiles: (isUploadingFiles: boolean) => void;
	relatedFilesInDeal: FindRelatedFilesRes[] | undefined;
	setNbrFilesUploaded: Dispatch<SetStateAction<number>>;
	setNbrFiles: (nbrFiles: number) => void;
}) => {
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();

	const createFileUploadPayload = (props: {
		relatedFiles?: FindRelatedFilesRes[];
		processingFiles?: ProcessingFile[];
	}) => {
		const relatedFilesToProcess = props.relatedFiles ?? relatedFilesInDeal;
		const filePayload = props.processingFiles ?? processingFiles;
		if (!relatedFilesToProcess || relatedFilesToProcess.length === 0) {
			return {
				documentLinksToCreate: [],
				processingFilesWithDocumentLinks: [],
			};
		}

		const documentLinksToCreate: DocumentLinkToCreate[] = [];
		const processingFilesWithDocumentLinks: ProcessingFile[] = [];

		for (const processingFile of filePayload) {
			const duplicateFile = relatedFilesToProcess.find(
				(file) => file.md5 === processingFile.md5
			);

			if (!duplicateFile) {
				continue;
			}

			const { matchedDocuments, relatedTransactions } = duplicateFile;

			// we don't need to re create it just link to the existing document
			const [matchedDocument] = matchedDocuments;
			if (matchedDocument) {
				for (const relatedTransaction of relatedTransactions) {
					// link it to any additional entities, specific to transactions for now
					if (relatedTransaction.shouldLink) {
						documentLinksToCreate.push({
							documentId: matchedDocument.documentId,
							transactionId: relatedTransaction.transactionId,
						});
					}
				}

				// link it to the entity where the upload started
				if (target) {
					documentLinksToCreate.push({
						documentId: matchedDocument.documentId,
						...target,
					});
				}

				continue;
			}

			const linksToCreate = relatedTransactions
				.filter((relatedTransaction) => relatedTransaction.shouldLink)
				.map((relatedTransaction) => ({
					transactionId: relatedTransaction.transactionId,
				}));

			processingFilesWithDocumentLinks.push({
				...processingFile,
				classifierName: duplicateFile.classifierName,
				dealClassifierId: duplicateFile.dealClassifierId,
				md5: duplicateFile.md5,
				documentLinks: linksToCreate,
			});
		}

		return {
			documentLinksToCreate,
			processingFilesWithDocumentLinks,
		};
	};

	/**
	 * 1. This will get a pre signed URL for the file to be upload
	 * 2. Upload the file to s3 using the pre signed URL
	 * 3. This will then also check the file uploaded is the same md5 as the one stored in aws
	 * ensuring the file that was uploaded is the one that is stored and linked in aws
	 * 4. Upload the file in postgres along with the links
	 */
	const uploadFiles = async (props: {
		relatedFiles?: FindRelatedFilesRes[];
		processingFiles?: ProcessingFile[];
	}) => {
		setIsUploadingFiles(true);
		setNbrFilesUploaded(0);

		const { documentLinksToCreate, processingFilesWithDocumentLinks } = createFileUploadPayload(
			{ relatedFiles: props.relatedFiles, processingFiles: props.processingFiles }
		);

		// every file was already in the deal and nothing was ticked to link. Bail before
		// the pre-signed-URL round trip so we never report a successful upload for zero work.
		if (documentLinksToCreate.length === 0 && processingFilesWithDocumentLinks.length === 0) {
			setProcessingFiles([]);
			setNbrFiles(0);
			setIsUploadingFiles(false);
			return;
		}

		setProcessingFiles(processingFilesWithDocumentLinks);

		setNbrFiles(processingFilesWithDocumentLinks.length);

		// Drives only the S3 folder path; the DocumentLink itself comes from linkTarget.
		const entityMap: DocumentFolderEntityMap = {};
		if (target?.personId) {
			entityMap.Person = target.personId;
		}
		if (target?.transactionId) {
			entityMap.Transaction = target.transactionId;
		}

		const fileData = processingFilesWithDocumentLinks.map((file) => ({
			fileName: cleanFileName(file.file.name),
			mimeType: file.file.type,
			extension: DocumentService.getFileExtensionFromName(file.file.name),
		}));

		const preSignedUrls = await DocumentService.createPreSignedUrls({
			fileData,
			dealId,
			entityMap,
		});

		const documentToCreate: CreateDocumentsDto['fileData'] = [];
		for (const processingFile of processingFilesWithDocumentLinks) {
			const preSignedUrlData = preSignedUrls.find(
				(url) => url.fileName === cleanFileName(processingFile.file.name)
			);

			if (!preSignedUrlData?.fileName) {
				showToastV2({
					title: tCapManage('documents.errors.filed-to-upload'),
					state: 'error',
				});
				throw new Error(`Could not upload file ${processingFile.file.name}`);
			}

			const [awsMd5] = await Promise.all([
				DocumentService.putUploadToPreSignedUrl({
					preSignedUrl: preSignedUrlData?.uploadPreSignedUrl,
					file: processingFile.file,
				}),
			]);

			if (awsMd5 !== `"${processingFile.md5}"`) {
				showToastV2({
					title: `${tCapManage('documents.upload-document.file-failed')}: ${processingFile.file.name}`,
					state: 'error',
				});
				throw new Error(`Issue with file upload: ${processingFile.file.name}`);
			}

			documentToCreate.push({
				md5: processingFile.md5,
				fileName: preSignedUrlData.fileName,
				fileKey: preSignedUrlData.fileKey,
				mimeType: processingFile.file.type,
				fileExtension: DocumentService.getFileExtensionFromName(processingFile.file.name),
				dealClassifierId: processingFile.dealClassifierId,
				documentLinks: processingFile.documentLinks,
			});
			setNbrFilesUploaded((progress) => progress + 1);
		}

		await DocumentService.createDocuments({
			dealId,
			linkTarget: target,
			fileData: documentToCreate,
			documentsToLink: documentLinksToCreate,
		});

		showToastV2({
			title: tCapManage('documents.upload-document.success'),
			state: 'success',
		});

		// refetch list
		await queryClient.refetchQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, {
					dealId,
				}) as string,
			],
		});

		await queryClient.refetchQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
					dealId,
				}) as string,
			],
		});

		setIsUploadingFiles(false);
	};

	return {
		uploadFiles,
	};
};
